import React, { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const FedaPayDepotButton = ({ plan, onSuccess, disabled = false }) => {
  const buttonRef = useRef(null);
  const { user } = useAuth();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const existing = document.getElementById("fedapay-checkout");
    if (existing) {
      setScriptLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdn.fedapay.com/checkout.js?v=1.1.7";
    script.id = "fedapay-checkout";
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => console.error("[FedaPay] ❌ Erreur chargement script dépôt");
    document.body.appendChild(script);
  }, []);

  const cleanupFedaPay = () => {
    try {
      if (window.FedaPay) {
        window.FedaPay.destroy();
      }
      // Fermer tout modal encore ouvert
      document.body.style.overflow = 'auto';
      const modals = document.querySelectorAll('[class*="modal"], [role="dialog"]');
      modals.forEach(modal => {
        if (modal.style.display !== 'none') {
          modal.style.display = 'none';
        }
      });
    } catch (e) {
      console.log('[FedaPay] Erreur cleanup:', e.message);
    }
    setIsInitialized(false);
  };

  const validatePhone = (raw) => {
    const fallback = "97223174";
    if (!raw) return fallback;
    const clean = raw.toString().replace(/[\s\-]/g, "");
    if (clean.startsWith("+229") || clean.startsWith("229")) return clean.slice(-8);
    if (clean.startsWith("0")) return clean.slice(1);
    return clean;
  };

  useEffect(() => {
    if (!scriptLoaded || !buttonRef.current || !window.FedaPay || !plan || !user) return;
    if (isInitialized || isProcessing) return;

    try {
      window.FedaPay.destroy();
    } catch (e) {
      console.log('[FedaPay] Erreur destroy:', e.message);
    }

    const effectiveEmail = user?.email || "client@example.com";
    const derivedFirst = user?.first_name || user?.user_metadata?.first_name || "Client";
    const derivedLast = user?.last_name || user?.user_metadata?.last_name || "Campus";
    const effectivePhone = validatePhone(user?.phone_number || user?.phone);
    const amount = parseInt(plan.fixed_amount, 10);

    window.FedaPay.init(buttonRef.current, {
      public_key: process.env.REACT_APP_FEDAPAY_PUBLIC_KEY,
      transaction: {
        amount,
        description: `Dépôt épargne - ${plan.plan_name || "Plan"} - ${effectiveEmail}`,
        currency: { iso: "XOF" },
        custom_metadata: {
          paymentType: "savings_deposit",
          user_id: String(user?.id || ""),
          plan_id: String(plan.id || ""),
          amount: String(amount),
        },
      },
      customer: {
        email: effectiveEmail,
        firstname: derivedFirst,
        lastname: derivedLast,
        phone_number: {
          number: effectivePhone,
          country: "BJ",
        },
      },
      modal: true,

      onComplete: ({ reason, transaction }) => {
        console.log("[FedaPay Dépôt] onComplete", reason, transaction);
        if (reason === window.FedaPay.CHECKOUT_COMPLETED) {
          setIsProcessing(true);
          cleanupFedaPay();
          if (typeof onSuccess === "function") {
            onSuccess({ transaction });
          }
        } else {
          cleanupFedaPay();
        }
      },

      onError: (error) => {
        console.error("[FedaPay Dépôt] Erreur ❌", error);
        cleanupFedaPay();
      },

      onClose: () => {
        console.log("[FedaPay Dépôt] Modal fermée");
        cleanupFedaPay();
      },
    });

    setIsInitialized(true);
  }, [scriptLoaded, plan, user, isInitialized, isProcessing, onSuccess]);

  return (
    <button
      ref={buttonRef}
      disabled={!scriptLoaded || !plan || disabled || isProcessing}
      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
    >
      <Plus className="w-5 h-5 mr-2" />
      {isProcessing ? 'Traitement...' : 'Dépôt'}
    </button>
  );
};

export default FedaPayDepotButton;
